import { supabase, allowOfflineFallback } from './mockData';
import { Vaccination } from '../types';

export async function getVaccinations(): Promise<Vaccination[]> {
  try {
    const { data, error } = await supabase
      .from('vaccinations')
      .select(`
        *,
        pet:pets(id, name, species, breed, owner_id, owner:owners(id, name, phone, email))
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw new Error(error.message);
    
    return (data || []).map((item: any) => ({
      id: item.id,
      pet_id: item.pet_id || item.petId,
      name: item.name || '',
      date: item.date || '',
      next_due_date: item.next_due_date || '',
      status: item.status || 'completed',
      notes: item.notes || '',
      vaccine_name: item.name || '', // alias for backward compatibility
      pet: item.pet ? {
        id: item.pet.id,
        name: item.pet.name || '',
        species: item.pet.species || 'dog',
        breed: item.pet.breed || '',
        owner_id: item.pet.owner_id || '',
        owner: item.pet.owner ? {
          id: item.pet.owner.id,
          name: item.pet.owner.name || '',
          phone: item.pet.owner.phone || '',
          email: item.pet.owner.email || ''
        } : undefined
      } : undefined,
      createdAt: item.created_at || new Date().toISOString()
    })) as Vaccination[];
  } catch (error) {
    console.error('getVaccinations:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const { mockVaccinations, getVaccinationWithPet } = await import('./mockData');
    return mockVaccinations.map(vaccination => {
      const vaccinationWithPet = getVaccinationWithPet(vaccination.id);
      return vaccinationWithPet || vaccination;
    });
  }
}

export async function addVaccination(vaccination: Omit<Vaccination, 'id' | 'createdAt'>): Promise<Vaccination> {
  try {
    const vaccinationData = {
      pet_id: vaccination.pet_id || vaccination.petId,
      name: vaccination.name || vaccination.vaccine_name,
      date: vaccination.date,
      next_due_date: vaccination.next_due_date || null,
      status: vaccination.status,
      notes: vaccination.notes,
    };

    const { data, error } = await supabase.from('vaccinations').insert([vaccinationData]).select().single();
    if (error) throw new Error(error.message);
    
    return {
      ...vaccination,
      id: data.id,
      createdAt: data.created_at || new Date().toISOString()
    } as Vaccination;
  } catch (error) {
    console.error('vaccinations insert:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const newVaccination: Vaccination = {
      ...vaccination,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };
    
    const existingVaccinations = JSON.parse(localStorage.getItem('vaccinations') || '[]');
    existingVaccinations.push(newVaccination);
    localStorage.setItem('vaccinations', JSON.stringify(existingVaccinations));
    
    return newVaccination;
  }
}

export async function updateVaccination(id: string, vaccination: Partial<Vaccination>): Promise<Vaccination> {
  try {
    const updateData: any = {};
    
    if (vaccination.pet_id !== undefined) updateData.pet_id = vaccination.pet_id;
    if (vaccination.name !== undefined) updateData.name = vaccination.name;
    if (vaccination.vaccine_name !== undefined) updateData.name = vaccination.vaccine_name; // map to name column
    if (vaccination.date !== undefined) updateData.date = vaccination.date;
    // Boş ('') gönderilirse tarih temizlenir (undefined = alana dokunma)
    if (vaccination.next_due_date !== undefined) updateData.next_due_date = vaccination.next_due_date || null;
    if (vaccination.status !== undefined) updateData.status = vaccination.status;
    if (vaccination.notes !== undefined) updateData.notes = vaccination.notes;

    const { data, error } = await supabase.from('vaccinations').update(updateData).eq('id', id).select().single();
    if (error) throw new Error(error.message);
    
    return {
      ...vaccination,
      id: data.id,
      createdAt: data.created_at || new Date().toISOString()
    } as Vaccination;
  } catch (error) {
    console.error('vaccinations update:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const existingVaccinations = JSON.parse(localStorage.getItem('vaccinations') || '[]');
    const vaccinationIndex = existingVaccinations.findIndex((v: Vaccination) => v.id === id);
    
    if (vaccinationIndex !== -1) {
      existingVaccinations[vaccinationIndex] = { ...existingVaccinations[vaccinationIndex], ...vaccination };
      localStorage.setItem('vaccinations', JSON.stringify(existingVaccinations));
      return existingVaccinations[vaccinationIndex];
    }
    
    throw new Error('Vaccination bulunamadı');
  }
}

export async function deleteVaccination(id: string): Promise<void> {
  try {
    const { error } = await supabase.from('vaccinations').delete().eq('id', id);
    if (error) throw new Error(error.message);
  } catch (error) {
    console.error('vaccinations delete:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const existingVaccinations = JSON.parse(localStorage.getItem('vaccinations') || '[]');
    const filteredVaccinations = existingVaccinations.filter((v: Vaccination) => v.id !== id);
    localStorage.setItem('vaccinations', JSON.stringify(filteredVaccinations));
  }
}